import { useEffect, useMemo, useState } from "react";
import { PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { ProveedorAdmin } from "@/lib/types";
import { useProveedoresAdmin } from "../hooks/use-proveedores-admin";
import { listarProveedoresAdmin } from "../services/proveedores-service";
import { ProveedoresHeader } from "./ProveedoresHeader";
import { ProveedoresFiltros } from "./ProveedoresFiltros";
import { ProveedorCrearForm } from "./ProveedorCrearForm";
import { SeleccionarTipoProveedorModal, type TipoProveedor } from "./SeleccionarTipoProveedorModal";
import { ProveedoresDesktopTable } from "./ProveedoresDesktopTable";
import { ProveedoresMobileList } from "./ProveedoresMobileList";

const PAGE_SIZE = 15;

/**
 * Página de proveedores · listado + filtros + form de creación.
 * Filtros (texto + solo activos) y paginado se resuelven en el SERVIDOR
 * (query params con debounce de 350ms) · el cliente solo pinta la página.
 * Dos presentaciones (desktop table / mobile cards).
 */
export function AdminProveedoresPage() {
  const [filtro, setFiltro] = useState("");
  const [soloActivos, setSoloActivos] = useState(false);
  // Alta en dos pasos: primero el modal elige el tipo, luego se muestra el form
  // con los campos/validaciones de ese tipo. `tipoCrear === null` = sin alta en curso.
  const [modalTipoAbierto, setModalTipoAbierto] = useState(false);
  const [tipoCrear, setTipoCrear] = useState<TipoProveedor | null>(null);
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  const filtrosServer = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      ...(soloActivos ? { soloActivos: true } : {}),
    }),
    [filtroDebounced, soloActivos]
  );

  useEffect(() => {
    setPage(1);
  }, [filtroDebounced, soloActivos]);

  const { data } = useProveedoresAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodos = async (): Promise<ProveedorAdmin[]> => {
    const todos: ProveedorAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarProveedoresAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <ProveedoresHeader
        total={data?.totalCatalogo ?? 0}
        activos={data?.totalActivos ?? 0}
        modoCrear={tipoCrear !== null}
        onIniciarCrear={() => setModalTipoAbierto(true)}
      />
      <SeleccionarTipoProveedorModal
        open={modalTipoAbierto}
        onOpenChange={setModalTipoAbierto}
        onSeleccionar={(tipo) => {
          setTipoCrear(tipo);
          setModalTipoAbierto(false);
        }}
      />
      <Stack gap="md">
        {tipoCrear !== null ? (
          <ProveedorCrearForm
            numeroSugerido={data?.siguienteNumero ?? 1}
            tipo={tipoCrear}
            onSalir={() => setTipoCrear(null)}
            onCambiarTipo={() => setModalTipoAbierto(true)}
          />
        ) : null}
        <ProveedoresFiltros texto={filtro} onTextoChange={setFiltro} soloActivos={soloActivos} onSoloActivosChange={setSoloActivos} />
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <ProveedoresDesktopTable items={items} obtenerFilas={obtenerTodos} />
        <ProveedoresMobileList items={items} />
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="proveedores" />
      </Stack>
    </div>
  );
}
